import React, { useState, useRef, useEffect, useMemo } from "react";

import { Row, Col, Form, Typography, Space, Button, Spin } from "antd";
import {
  FormHiddenSubmitButton,
  FormInput,
} from "components/FormItems/FlatFormItems";


import { ReactComponent as CheckMarkIcon } from "assets/icons/checkMarkIco.svg";
import { ReactComponent as CloseIcon } from "assets/icons/closeIco.svg";
import { useDispatch } from "react-redux";
import { applyVoucher, removeVoucher } from "services/booking.service";
import { setLoading } from "redux/actions/loading.actions";
import useDefaultPayment from "hooks/useDefaultPayment";
import { getPaymentMethodById } from "services/paymentMethod.service";
import { checkIfRewardIsApplied } from "utils/common";
import { AppleFilled, GoogleCircleFilled } from "@ant-design/icons";
import { MERCHANT } from "utils/enums";


const { Title, Text, Link } = Typography;

// Shows Issuer Card Icon and Card Number
const MiniCard = ({ brand, last4Digits, isLoading, type }) => {
  if (isLoading) {
    return <Spin />;
  }

  return (
    <div className="miniPaymentCard">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {
          type === MERCHANT.GooglePay && <GoogleCircleFilled style={{ fontSize: 24, color: '#373636' }} />
        }
        {
          type === MERCHANT.ApplePay && <AppleFilled style={{ fontSize: 24, color: '#373636' }} />
        }

        <span className={`cardChip issuer-${brand}`}>&nbsp;</span>
      </div>
      <Text>
        <span className="issuer"> {brand} </span> ending in {last4Digits}
      </Text>
    </div>
  );
};

const BookingPayment = ({ order, paymentMethods }) => {
  const [form] = Form.useForm();
  const submitBtnRef = useRef();

  useEffect(() => {
    form.setFieldsValue({ voucher: order?.voucherCode });
  }, [order]);
  const dispatch = useDispatch();

  let paymentMethod = useDefaultPayment({
    fetch: order?.orderId ? false : true,
    // orderId: order?.orderId,
  });

  if (order?.orderId !== undefined || order?.orderId !== null) {
    paymentMethod = paymentMethods?.find(
      (paymentMethod) =>
        paymentMethod?.paymentMethodId === order?.paymentMethodId
    );
  }

  const isBookingCompleted = order?.orderStatus === "Completed";
  const isBookingCancelled = order?.orderStatus === "Cancel";

  const isVoucherDisabled = useMemo(() => {
    if (order?.useWallet && order?.giftCardAmount > 0) {
      return true;
    }
    if (checkIfRewardIsApplied(order)) {
      return true;
    }
    return false;
  }, [order]);


  const [paymentMethodDetail, setPaymentMethodDetail] = useState({
    data: null,
    isLoading: true,
  });

  useEffect(() => {
    if (isBookingCompleted || isBookingCancelled) {
      const paymentMethodId = order?.paymentMethodId;
      if (paymentMethodId) {
        dispatch(
          getPaymentMethodById(paymentMethodId, false, (res) => {
            setPaymentMethodDetail({
              data: res?.data,
              isLoading: false,
            });
          })
        ).catch(() => {
          setPaymentMethodDetail({
            data: null,
            isLoading: false,
          });
        });
      }
    }
  }, [order, isBookingCompleted, isBookingCancelled]);

  return (
    <>
      <Row gutter={[16, 0]}>
        <Col span={10}>
          <MiniCard
            // isLoading={isBookingCompleted && paymentMethodDetail?.isLoading}
            brand={
              isBookingCompleted || isBookingCancelled
                ? paymentMethodDetail?.data?.brand ?? "NA"
                : paymentMethod?.brand ?? "NA"
            }
            last4Digits={
              isBookingCompleted || isBookingCancelled
                ? paymentMethodDetail?.data?.last4Digits ?? "0000"
                : paymentMethod?.last4Digits ?? "0000"
            }
            type={
              isBookingCompleted || isBookingCancelled
                ? paymentMethodDetail?.data?.type ?? "NA"
                : paymentMethod?.type ?? "NA"
            }
          />
        </Col>
        <Col span={14}>
          <div className="flex" style={{}}>
            <div className="info">i</div>
            <Text
              className="tradeGothic"
              style={{ fontSize: 14, color: "#7F889A" }}
            >
              Your card will not be charged until after the service is complete.
            </Text>
          </div>
        </Col>
        {/* <Col span={2} className="flex justifyEnd">
          <Link
            underline
            style={{ padding: 0 }}
            onClick={() => dispatch(push("account/payments"))}
            disabled={isBookingCompleted || isBookingCancelled}
          >
            Change
          </Link>
        </Col> */}
      </Row>
      <Row gutter={[16, 16]} className="couponsRow">
        <Col span={10}>
          <Form
            name="voucherForm"
            layout="vertical"
            form={form}
            requiredMark={false}
            onFinishFailed={({ values, errorFields, outOfDate }) => {
              form.scrollToField(errorFields[0].name, {
                scrollMode: "if-needed",
                block: "center",
                behavior: "smooth",
              });
            }}
            onFinish={async (values) => {
              dispatch(setLoading(true));
              await dispatch(applyVoucher(order.orderId, values.voucher));
              dispatch(setLoading(false));
            }}
          >
            <FormInput
              name="voucher"
              disabled={
                order?.voucherCode ||
                // disableVoucher ||
                isBookingCancelled ||
                isBookingCompleted
              }
              label="Apply Coupon"
              variant="underlined"
              placeholder="Eg: Code"
              formItemStyles={{ marginBottom: 0 }}
              initialValue={order?.voucherCode}
            />
            <FormHiddenSubmitButton submitRef={submitBtnRef}>
              submit
            </FormHiddenSubmitButton>
          </Form>
        </Col>
        <Col span={14}>
          {order?.voucherCode ? (
            <Space className="voucherAppliedContainer">
              <div className="checkMarkOutlined">
                <CheckMarkIcon />
              </div>
              <Title className="tradeGothic availedText">Applied</Title>
              <Button
                shape="circle"
                className="voucherCloseBtn"
                hidden={
                  order?.orderStatus === "Completed" ||
                  order?.orderStatus === "Cancel" ||
                  (!!order?.voucherCode && order?.voucherDiscount < 0)
                }
                onClick={async () => {
                  dispatch(setLoading(true));
                  await dispatch(removeVoucher(order?.orderId));
                  dispatch(setLoading(false));
                }}
              >
                <CloseIcon />
              </Button>
            </Space>
          ) : (
            <Link
              underline
              style={{ padding: 0 }}
              className="couponsRowApplyBtn"
              onClick={() => {
                if (submitBtnRef.current && !order?.voucherCode)
                  submitBtnRef.current.click();
              }}
              disabled={
                // disableVoucher ||
                isBookingCancelled || isBookingCompleted || isVoucherDisabled
              }
            >
              Apply Now
            </Link>
          )}
        </Col>
      </Row>
    </>
  );
};

BookingPayment.propTypes = {};

export default BookingPayment;
